import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { TFieldSchema, TFieldScope, TFieldType } from '../schemas/payment-provider-schema';

type FieldEditDialogProps = {
  open: boolean;
  mode: 'add' | 'edit';
  scope: TFieldScope;
  initial?: TFieldSchema;
  onClose: () => void;
  onSave: (payload: {
    id: string;
    type: TFieldType;
    scope: TFieldScope;
    required: boolean;
    displayOrder: number;
    label?: string;
  }) => void;
  isSaving?: boolean;
};

const FieldEditDialog = ({
  open,
  mode,
  scope,
  initial,
  onClose,
  onSave,
  isSaving,
}: FieldEditDialogProps) => {
  const [id, setId] = useState('');
  const [type, setType] = useState<TFieldType>('text');
  const [label, setLabel] = useState('');
  const [required, setRequired] = useState(false);
  const [displayOrder, setDisplayOrder] = useState<number>(1);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setId(initial.id);
      setType(initial.type);
      setLabel(initial.label || '');
      setRequired(!!initial.required);
      setDisplayOrder(initial.displayOrder ?? 1);
    } else {
      setId('');
      setType('text');
      setLabel('');
      setRequired(false);
      setDisplayOrder(1);
    }
  }, [open, mode, initial]);

  const handleSave = () => {
    onSave({
      id: id.trim(),
      type,
      scope,
      required,
      displayOrder,
      label: label.trim() || undefined,
    });
  };

  const isValid = mode === 'edit' ? true : /^[a-z][a-z0-9_-]{0,63}$/.test(id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={styles.title}>
        {mode === 'add' ? `Add ${scope} field` : `Edit field — ${id}`}
        <Typography sx={styles.subtitle}>
          {mode === 'add'
            ? 'id, type and scope are immutable after creation.'
            : 'Only label, required and display order can be changed.'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={styles.body}>
          <TextField
            label="Field ID *"
            size="small"
            fullWidth
            value={id}
            onChange={(e) => setId(e.target.value)}
            disabled={mode === 'edit' || isSaving}
            helperText={
              mode === 'add'
                ? 'lowercase, kebab/snake. e.g. api_key, merchant_code'
                : 'Immutable'
            }
          />
          <TextField
            select
            label="Type *"
            size="small"
            fullWidth
            value={type}
            onChange={(e) => setType(e.target.value as TFieldType)}
            disabled={mode === 'edit' || isSaving}
            helperText={mode === 'add' ? 'Immutable after create' : 'Immutable'}
          >
            <MenuItem value="text">text</MenuItem>
            <MenuItem value="secret">secret</MenuItem>
            <MenuItem value="number">number</MenuItem>
            <MenuItem value="boolean">boolean</MenuItem>
          </TextField>
          <TextField
            label="Label (optional)"
            size="small"
            fullWidth
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={isSaving}
            placeholder="Pretty label shown to ops"
          />
          <TextField
            label="Display order"
            size="small"
            type="number"
            fullWidth
            value={displayOrder}
            onChange={(e) => setDisplayOrder(Number(e.target.value) || 0)}
            disabled={isSaving}
          />
          <FormControlLabel
            control={
              <Switch
                checked={required}
                onChange={(e) => setRequired(e.target.checked)}
                disabled={isSaving}
              />
            }
            label="Required"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button sx={styles.cancelBtn} onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          sx={styles.saveBtn}
          onClick={handleSave}
          disabled={!isValid || isSaving}
        >
          {isSaving ? 'Saving…' : mode === 'add' ? 'Add Field' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FieldEditDialog;

const styles = {
  title: {
    fontFamily: 'GilroyBold',
    fontSize: 18,
  },
  subtitle: {
    fontFamily: 'GilroyRegular',
    fontSize: 12,
    color: '#64748b',
    mt: 0.5,
  },
  body: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    pt: 1,
  },
  actions: {
    px: 3,
    py: 2,
  },
  cancelBtn: {
    textTransform: 'capitalize' as const,
    color: '#000',
    fontFamily: 'GilroySemiBold',
    border: '1px solid #000',
    borderRadius: '8px',
    px: 2.5,
  },
  saveBtn: {
    textTransform: 'capitalize' as const,
    backgroundColor: '#000',
    color: '#FFF',
    fontFamily: 'GilroySemiBold',
    borderRadius: '8px',
    px: 2.5,
    '&:hover': { backgroundColor: '#333' },
    '&:disabled': { opacity: 0.5, color: '#FFF' },
  },
};
